import type { Chat, ClientState } from "@rhema/client";
import { useEffect } from "react";
import { useNavigate } from "react-router";
import { chatTitle, firstName, isMuted } from "../data/view";
import { useClient } from "../session/session";
import { getPrefs } from "./prefs";

/** Tab title with the unread count, such as "(3) Rhema". */
export function useUnreadTitle(unread: number) {
  useEffect(() => {
    document.title = unread > 0 ? `(${unread > 99 ? "99+" : unread}) Rhema` : "Rhema";
  }, [unread]);
}

/** What a local notification says. The text only appears when the person chose to show it. */
export function describeNotice(
  chat: Chat,
  state: Pick<ClientState, "me" | "previews">,
  showText: boolean,
): { title: string; body: string } {
  const meId = state.me?.id;
  const senderId = chat.last_message?.sender_id;
  const sender = chat.members.find((m) => m.user.id === senderId)?.user.display_name ?? "someone";
  const title = chatTitle(chat, meId);
  const preview = state.previews[chat.id];
  if (showText && preview) {
    return { title, body: chat.kind === "group" ? `${firstName(sender)}: ${preview}` : preview };
  }
  return { title, body: `New message from ${firstName(sender)}` };
}

async function show(title: string, body: string, chatId: string, open: () => void) {
  const options: NotificationOptions = { body, tag: chatId, icon: "/icon.svg", data: { chat_id: chatId } };
  // Through the service worker when there is one, so a click is handled the same way as a push.
  const registration = await navigator.serviceWorker?.getRegistration?.().catch(() => undefined);
  if (registration) {
    await registration.showNotification(title, options);
    return;
  }
  const n = new Notification(title, options);
  n.onclick = () => {
    window.focus();
    open();
    n.close();
  };
}

function wants(chat: Chat, meId: string | undefined): boolean {
  const last = chat.last_message;
  if (last?.kind !== "message" || !last.sender_id || last.sender_id === meId) return false;
  if (isMuted(chat) || chat.archived) return false;
  if (chat.mentions_only && !chat.mentioned) return false;
  return true;
}

/**
 * While the tab is open but hidden, shows a notification for each new message. It says who
 * wrote and nothing else, unless "Show message text in notifications" is on.
 */
export function useInAppNotifications() {
  const client = useClient();
  const navigate = useNavigate();

  // A click on a notification, passed on by the service worker, opens the chat in this tab.
  useEffect(() => {
    const sw = typeof navigator !== "undefined" ? navigator.serviceWorker : undefined;
    if (!sw?.addEventListener) return;
    const onMessage = (e: MessageEvent) => {
      const data = e.data as { type?: string; chat_id?: string } | null;
      if (data?.type === "open-chat" && data.chat_id) navigate(`/chats/${data.chat_id}`);
    };
    sw.addEventListener("message", onMessage);
    return () => sw.removeEventListener("message", onMessage);
  }, [navigate]);

  useEffect(() => {
    if (typeof Notification === "undefined") return;
    const seen = new Map<string, number>();
    for (const chat of Object.values(client.getState().chats)) seen.set(chat.id, chat.last_seq);
    const timers = new Set<ReturnType<typeof setTimeout>>();

    const unsubscribe = client.subscribe(() => {
      const state = client.getState();
      for (const chat of Object.values(state.chats)) {
        const before = seen.get(chat.id);
        seen.set(chat.id, Math.max(before ?? 0, chat.last_seq));
        if (before === undefined || chat.last_seq <= before) continue;
        if (document.visibilityState !== "hidden") continue;
        const prefs = getPrefs();
        if (prefs.enabled !== true || Notification.permission !== "granted") continue;
        if (!wants(chat, state.me?.id)) continue;
        // Give the preview a moment to decrypt.
        const timer = setTimeout(() => {
          timers.delete(timer);
          const latest = client.getState();
          const current = latest.chats[chat.id];
          if (!current || document.visibilityState !== "hidden") return;
          const { title, body } = describeNotice(current, latest, getPrefs().showText);
          void show(title, body, chat.id, () => navigate(`/chats/${chat.id}`)).catch(() => undefined);
        }, 400);
        timers.add(timer);
      }
    });
    return () => {
      unsubscribe();
      for (const t of timers) clearTimeout(t);
    };
  }, [client, navigate]);
}
